import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, CheckCircle, XCircle, Plus, Minus, Download, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';
const faqs = [{
  q: 'How does ducted air conditioning work?',
  a: 'The main unit is hidden within a suspended ceiling or roof space. Conditioned air is then distributed through concealed ductwork and delivered into the room via discreet grilles or diffusers.'
}, {
  q: 'Is it noisy?',
  a: 'No, ducted systems are among the quietest commercial options available. Because the fan coil unit is hidden in the ceiling void, operational noise is significantly muffled, making it ideal for quiet office environments.'
}, {
  q: 'How much ceiling void space is required?',
  a: 'Typically, you need a minimum of 300mm to 400mm of clearance above the suspended ceiling to accommodate the indoor unit and ductwork, though slimline units are available for tighter spaces.'
}, {
  q: 'Can a ducted system provide heating as well as cooling?',
  a: 'Yes, modern commercial ducted systems are heat pumps, meaning they provide highly efficient heating during winter and cooling during summer from the same system.'
}, {
  q: 'What is the maintenance requirement?',
  a: 'Like all commercial systems, they require regular maintenance (usually bi-annually). Engineers will need access hatches in the ceiling to reach the hidden units for filter cleaning and servicing.'
}];
export function Ducted() {
  const [openFaqId, setOpenFaqId] = useState<number | null>(null);
  return <main className="min-h-screen font-sans text-primary-dark" data-id="element-2102">
      {/* 1. Hero Section */}
      <section className="bg-[#E8F0F8] pt-36 pb-20" data-id="element-2103">
        <div className="container mx-auto px-6" data-id="element-2104">
          <div className="grid lg:grid-cols-12 gap-12 items-start" data-id="element-2105">
            <div className="lg:col-span-7" data-id="element-2106">
              <span className="text-sm uppercase tracking-wider font-medium text-primary-dark/50 mb-6 block" data-id="element-2107">
                Systems / Ducted
              </span>
              <motion.h1 initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6
            }} className="text-4xl md:text-5xl lg:text-6xl font-serif leading-tight text-primary-dark mb-6" data-id="element-2108">
                Ducted air conditioning
              </motion.h1>
              <motion.p initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.1
            }} className="text-lg text-primary-dark/70 leading-relaxed mb-10" data-id="element-2109">
                Discreet, hidden air conditioning for commercial spaces. Enjoy
                powerful, even climate control without compromising your
                interior design.
              </motion.p>
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.2
            }} className="flex flex-wrap gap-4" data-id="element-2110">
                <Link to="/contact" className="bg-accent hover:bg-accent/90 text-white px-8 py-3.5 rounded-full font-medium transition-colors" data-id="element-2111">
                  Book a site survey
                </Link>
                <Link to="/contact" className="bg-white hover:bg-primary-dark/5 text-primary-dark border border-primary-dark/10 px-8 py-3.5 rounded-full font-medium transition-colors" data-id="element-2112">
                  Speak to our team
                </Link>
              </motion.div>
            </div>
            <div className="lg:col-span-5" data-id="element-2113">
              <motion.div initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.6
            }} className="rounded-3xl overflow-hidden aspect-[4/3] shadow-lg" data-id="element-2114">
                <img src="https://cdn.magicpatterns.com/uploads/74DBnnnPaDiHoNeUuZD5vA/ducted-air-conditoning-accutemp.png" alt="Ducted air conditioning in a modern office" className="w-full h-full object-cover" data-id="element-2115" />
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Best For */}
      <section className="bg-white py-16 border-b border-primary-dark/5" data-id="element-2116">
        <div className="container mx-auto px-6" data-id="element-2117">
          <div className="max-w-4xl mx-auto text-center" data-id="element-2118">
            <h2 className="text-sm font-bold uppercase tracking-widest text-primary-dark/40 mb-6" data-id="element-2119">
              Best for
            </h2>
            <p className="text-xl md:text-2xl text-primary-dark/80 leading-relaxed font-medium" data-id="element-2120">
              Offices, retail fitouts, premium commercial interiors, listed
              buildings, and spaces where aesthetics and quiet operation matter
              most.
            </p>
          </div>
        </div>
      </section>

      {/* 3. Key Benefits */}
      <section className="bg-[#E8F0F8]/50 py-24" data-id="element-2121">
        <div className="container mx-auto px-6" data-id="element-2122">
          <div className="max-w-4xl mx-auto" data-id="element-2123">
            <h2 className="text-3xl md:text-4xl font-serif mb-10 text-center" data-id="element-2124">
              Key benefits
            </h2>
            <div className="grid md:grid-cols-2 gap-6" data-id="element-2125">
              {['Hidden from view (only grilles are visible)', 'Exceptionally quiet operation', 'Even temperature distribution across large areas', 'Zoned control possible for different areas', 'Integrates seamlessly with VRV/VRF systems', 'Long lifespan with proper maintenance'].map((benefit, i) => <div key={i} className="flex items-start gap-4 bg-white p-6 rounded-2xl shadow-sm" data-id="element-2126">
                  <CheckCircle className="w-6 h-6 text-accent shrink-0" data-id="element-2127" />
                  <span className="font-medium text-primary-dark/80" data-id="element-2128">
                    {benefit}
                  </span>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* 4. Is this the right fit? */}
      <section className="bg-white py-24" data-id="element-2129">
        <div className="container mx-auto px-6" data-id="element-2130">
          <h2 className="text-3xl md:text-4xl font-serif mb-16 text-center" data-id="element-2131">
            Is this the right fit?
          </h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto" data-id="element-2132">
            <div className="bg-[#E8F0F8] p-10 rounded-3xl border border-primary-dark/5" data-id="element-2133">
              <h3 className="text-2xl font-serif mb-6 flex items-center gap-3 text-success" data-id="element-2134">
                <CheckCircle className="w-7 h-7" data-id="element-2135" />
                Good fit for
              </h3>
              <ul className="space-y-4" data-id="element-2136">
                <li className="flex items-start gap-3 text-primary-dark/80" data-id="element-2137">
                  <div className="w-1.5 h-1.5 rounded-full bg-success mt-2 shrink-0" data-id="element-2138" />
                  <span data-id="element-2139">Offices needing clean, uninterrupted aesthetics</span>
                </li>
                <li className="flex items-start gap-3 text-primary-dark/80" data-id="element-2140">
                  <div className="w-1.5 h-1.5 rounded-full bg-success mt-2 shrink-0" data-id="element-2141" />
                  <span data-id="element-2142">High-end retail stores and showrooms</span>
                </li>
                <li className="flex items-start gap-3 text-primary-dark/80" data-id="element-2143">
                  <div className="w-1.5 h-1.5 rounded-full bg-success mt-2 shrink-0" data-id="element-2144" />
                  <span data-id="element-2145">Restaurants and premium hospitality venues</span>
                </li>
                <li className="flex items-start gap-3 text-primary-dark/80" data-id="element-2146">
                  <div className="w-1.5 h-1.5 rounded-full bg-success mt-2 shrink-0" data-id="element-2147" />
                  <span data-id="element-2148">Hotels and luxury accommodation</span>
                </li>
              </ul>
            </div>
            <div className="bg-white p-10 rounded-3xl border border-primary-dark/10 shadow-sm" data-id="element-2149">
              <h3 className="text-2xl font-serif mb-6 flex items-center gap-3 text-error" data-id="element-2150">
                <XCircle className="w-7 h-7" data-id="element-2151" />
                May not be ideal for
              </h3>
              <ul className="space-y-4" data-id="element-2152">
                <li className="flex items-start gap-3 text-primary-dark/80" data-id="element-2153">
                  <div className="w-1.5 h-1.5 rounded-full bg-error mt-2 shrink-0" data-id="element-2154" />
                  <span data-id="element-2155">Very small individual rooms</span>
                </li>
                <li className="flex items-start gap-3 text-primary-dark/80" data-id="element-2156">
                  <div className="w-1.5 h-1.5 rounded-full bg-error mt-2 shrink-0" data-id="element-2157" />
                  <span data-id="element-2158">Buildings with no suspended ceiling or roof void</span>
                </li>
                <li className="flex items-start gap-3 text-primary-dark/80" data-id="element-2159">
                  <div className="w-1.5 h-1.5 rounded-full bg-error mt-2 shrink-0" data-id="element-2160" />
                  <span data-id="element-2161">
                    Tight budgets where simpler wall-mounted systems work
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 5. AccuTemp design approach */}
      <section className="bg-primary-dark text-white py-24" data-id="element-2162">
        <div className="container mx-auto px-6" data-id="element-2163">
          <div className="max-w-4xl mx-auto text-center" data-id="element-2164">
            <h2 className="text-3xl md:text-4xl font-serif mb-8" data-id="element-2165">
              The AccuTemp design approach
            </h2>
            <p className="text-lg text-white/80 leading-relaxed" data-id="element-2166">
              We don't just install systems; we engineer solutions. Our in-house
              commercial team helps specify the exact ducted setup you need
              based on your room layout, comfort requirements, airflow dynamics,
              heat load, usage patterns, and budget. This design-led expertise
              ensures your system operates efficiently, quietly, and reliably
              for years to come.
            </p>
          </div>
        </div>
      </section>

      {/* 6. FAQs */}
      <section className="bg-[#E8F0F8] py-24" data-id="element-2167">
        <div className="container mx-auto px-6 max-w-3xl" data-id="element-2168">
          <h2 className="text-3xl md:text-4xl font-serif mb-12 text-center" data-id="element-2169">
            Frequently asked questions
          </h2>
          <div className="space-y-4" data-id="element-2170">
            {faqs.map((faq, i) => <div key={i} className="border border-primary-dark/10 rounded-2xl overflow-hidden bg-white" data-id="element-2171">
                <button onClick={() => setOpenFaqId(openFaqId === i ? null : i)} className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-primary-light/30 transition-colors" data-id="element-2172">
                  <span className="font-medium text-primary-dark pr-8" data-id="element-2173">
                    {faq.q}
                  </span>
                  <span className="flex-shrink-0 text-primary-dark/40" data-id="element-2174">
                    {openFaqId === i ? <Minus className="w-5 h-5" data-id="element-2175" /> : <Plus className="w-5 h-5" data-id="element-2176" />}
                  </span>
                </button>
                <AnimatePresence data-id="element-2177">
                  {openFaqId === i && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3
              }} data-id="element-2178">
                      <div className="px-6 pb-6 pt-2 text-primary-dark/70 leading-relaxed border-t border-primary-dark/5" data-id="element-2179">
                        <p data-id="element-2180">{faq.a}</p>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>)}
          </div>
        </div>
      </section>

      {/* 7. Brochure / literature section */}
      <section className="bg-white py-24 border-b border-primary-dark/5" data-id="element-2181">
        <div className="container mx-auto px-6 max-w-4xl text-center" data-id="element-2182">
          <h2 className="text-2xl font-serif mb-8" data-id="element-2183">Technical literature</h2>
          <div className="flex flex-col sm:flex-row justify-center gap-6" data-id="element-2184">
            <a href="https://accutemp.co.uk/products/Ducted-Medium%20Static-%20FBA-A9%20%20RXM.pdf" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-3 bg-[#E8F0F8] hover:bg-[#D4DDE8] text-primary-dark px-6 py-4 rounded-xl font-medium transition-colors" data-id="element-2185">
              <FileText className="w-5 h-5 text-accent" data-id="element-2186" />
              Download brochure
            </a>
            <a href="https://accutemp.co.uk/products/Commerical%20Cat.pdf" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-3 bg-[#E8F0F8] hover:bg-[#D4DDE8] text-primary-dark px-6 py-4 rounded-xl font-medium transition-colors" data-id="element-2187">
              <Download className="w-5 h-5 text-accent" data-id="element-2188" />
              View commercial catalogue
            </a>
          </div>
        </div>
      </section>

      {/* 8. Bottom CTA */}
      <section className="bg-[#D4DDE8] py-24 text-center" data-id="element-2189">
        <div className="container mx-auto px-6" data-id="element-2190">
          <div className="max-w-2xl mx-auto" data-id="element-2191">
            <h2 className="text-3xl md:text-4xl font-serif text-primary-dark mb-10" data-id="element-2192">
              Ready to explore ducted options?
            </h2>
            <div className="flex flex-wrap justify-center gap-4" data-id="element-2193">
              <Link to="/contact" className="group inline-flex items-center gap-3 bg-accent hover:bg-accent/90 text-white px-8 py-4 rounded-full font-medium transition-all duration-300" data-id="element-2194">
                Book a site survey
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-2195" />
              </Link>
              <Link to="/contact" className="inline-flex items-center gap-3 bg-white hover:bg-white/80 text-primary-dark px-8 py-4 rounded-full font-medium transition-all duration-300" data-id="element-2196">
                Get expert advice
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>;
}